import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../context/AuthContext';
import { createActivityNameMap, getActivities, getActivityName } from '../services/activityService';
import {
  createTrainingClassMap,
  getTrainingClassLabel,
  getTrainingClasses,
} from '../services/trainingClassService';
import { getTrainingSessions } from '../services/trainingSessionService';
import { formatDate, parseSessionDate, toDateKey } from './homePageUtils';
import './TrainingHistory.css';

export default function TrainingHistory() {
  const { accessToken, logout } = useAuth();
  const theme = useTheme();
  const isCompactFilters = useMediaQuery(theme.breakpoints.down('md'));
  const [sessions, setSessions] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [activityNamesById, setActivityNamesById] = useState({});
  const [allTrainingClasses, setAllTrainingClasses] = useState([]);
  const [trainingClassesByName, setTrainingClassesByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [classTypeFilter, setClassTypeFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minIntensity, setMinIntensity] = useState('');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      setError('Missing auth token. Please sign in again.');
      return;
    }

    const loadSessions = async () => {
      try {
        const [sessionsData, activitiesData, trainingClassesData] = await Promise.all([
          getTrainingSessions(accessToken),
          getActivities(accessToken),
          getTrainingClasses(accessToken),
        ]);
        const safeActivities = Array.isArray(activitiesData) ? activitiesData : [];
        const safeTrainingClasses = Array.isArray(trainingClassesData) ? trainingClassesData : [];
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setAllActivities(safeActivities);
        setActivityNamesById(createActivityNameMap(safeActivities));
        setAllTrainingClasses(safeTrainingClasses);
        setTrainingClassesByName(createTrainingClassMap(safeTrainingClasses));
      } catch (err) {
        if (err?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }
        setError(err?.message || 'Failed to load training history.');
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, [accessToken, logout]);

  const classTypeOptions = useMemo(() => {
    const uniqueClassTypes = new Set(
      sessions
        .map((s) => s?.sessionClass)
        .filter(Boolean)
    );
    return allTrainingClasses.filter((trainingClass) => uniqueClassTypes.has(trainingClass.name));
  }, [allTrainingClasses, sessions]);

  const activityOptions = useMemo(() => {
    const uniqueActivityIds = new Set();
    sessions.forEach((session) => {
      const activities = session?.SessionActivity || [];
      activities.forEach((activity) => {
        if (activity?.activityId) {
          uniqueActivityIds.add(activity.activityId);
        }
      });
    });

    return allActivities.filter((activity) => uniqueActivityIds.has(activity.id));
  }, [allActivities, sessions]);

  const filteredSessions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const minIntensityValue = Number(minIntensity);
    const hasMinIntensity = minIntensity !== '' && !Number.isNaN(minIntensityValue);

    return [...sessions]
      .filter((session) => {
        const activities = session?.SessionActivity || [];
        const sessionDateKey = toDateKey(session?.sessionDate);

        if (classTypeFilter !== 'all' && session?.sessionClass !== classTypeFilter) {
          return false;
        }

        if (
          activityFilter !== 'all'
          && !activities.some((activity) => String(activity.activityId) === activityFilter)
        ) {
          return false;
        }

        if (dateFrom && sessionDateKey && sessionDateKey < dateFrom) {
          return false;
        }

        if (dateTo && sessionDateKey && sessionDateKey > dateTo) {
          return false;
        }

        if (hasMinIntensity && (Number(session?.intensity) || 0) < minIntensityValue) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const activityText = activities
          .map((activity) => {
            const activityName = getActivityName(activityNamesById, activity.activityId);
            return `${activityName} ${activity.notes || ''}`;
          })
          .join(' ')
          .toLowerCase();

        const sessionText = [
          getTrainingClassLabel(trainingClassesByName, session?.sessionClass),
          session?.notes || '',
          activityText,
        ].join(' ').toLowerCase();

        return sessionText.includes(normalizedSearch);
      })
      .sort((a, b) => toDateKey(b.sessionDate).localeCompare(toDateKey(a.sessionDate)));
  }, [activityNamesById, sessions, searchTerm, classTypeFilter, activityFilter, dateFrom, dateTo, minIntensity, trainingClassesByName]);

  const clearFilters = () => {
    setSearchTerm('');
    setClassTypeFilter('all');
    setActivityFilter('all');
    setDateFrom('');
    setDateTo('');
    setMinIntensity('');
  };

  return (
    <Box className="training-history-page">
      <Paper className="training-history-header" elevation={1}>
        <Typography variant="h4" component="h1">Training History</Typography>
        <Typography variant="body2" color="text.secondary">
          Filter and review all training sessions.
        </Typography>
      </Paper>

      <Paper className="training-history-filters" elevation={1}>
        {isCompactFilters && (
          <Button
            variant="outlined"
            onClick={() => setShowFiltersMobile((prev) => !prev)}
            className="training-history-mobile-filter-btn"
            sx={{ mb: 1 }}
          >
            {showFiltersMobile ? 'Hide Filters' : 'Filter'}
          </Button>
        )}

        {(!isCompactFilters || showFiltersMobile) && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} className="training-history-filter-row">
            <TextField
              label="Search Notes"
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="training-history-filter"
            />

            <FormControl size="small" className="training-history-filter">
              <InputLabel id="class-type-filter-label">Class Type</InputLabel>
              <Select
                labelId="class-type-filter-label"
                value={classTypeFilter}
                label="Class Type"
                onChange={(event) => setClassTypeFilter(event.target.value)}
              >
                <MenuItem value="all">All Classes</MenuItem>
                {classTypeOptions.map((trainingClass) => (
                  <MenuItem key={trainingClass.name} value={trainingClass.name}>
                    {trainingClass.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" className="training-history-filter">
              <InputLabel id="activity-filter-label">Activity</InputLabel>
              <Select
                labelId="activity-filter-label"
                value={activityFilter}
                label="Activity"
                onChange={(event) => setActivityFilter(event.target.value)}
              >
                <MenuItem value="all">All Activities</MenuItem>
                {activityOptions.map((activity) => (
                  <MenuItem key={activity.id} value={String(activity.id)}>
                    {activity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From"
                format="MM/dd/yy"
                value={dateFrom ? parseSessionDate(dateFrom) : null}
                onChange={(newValue) => setDateFrom(newValue ? toDateKey(newValue) : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    className: 'training-history-filter',
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To"
                format="MM/dd/yy"
                value={dateTo ? parseSessionDate(dateTo) : null}
                onChange={(newValue) => setDateTo(newValue ? toDateKey(newValue) : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    className: 'training-history-filter',
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Min Intensity"
              type="number"
              size="small"
              inputProps={{ min: 1, max: 10 }}
              value={minIntensity}
              onChange={(event) => setMinIntensity(event.target.value)}
              className="training-history-filter training-history-intensity"
            />

            <Button variant="outlined" onClick={clearFilters} className="training-history-clear-btn">
              Clear
            </Button>
          </Stack>
        )}
      </Paper>

      {loading ? (
        <Box className="training-history-loading">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!error && (
            <Typography variant="body2" className="training-history-count">
              Showing {filteredSessions.length} of {sessions.length} sessions
            </Typography>
          )}

          {!error && filteredSessions.length === 0 ? (
            <Alert severity="info">No sessions match your filters.</Alert>
          ) : (
            <Stack spacing={1.5}>
              {filteredSessions.map((session) => {
                const activities = session?.SessionActivity || [];
                return (
                  <Accordion key={session.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box className="training-history-session-summary">
                        <Typography variant="subtitle1">{formatDate(session.sessionDate)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getTrainingClassLabel(trainingClassesByName, session.sessionClass)}
                        </Typography>
                        <Box className="training-history-summary-chips">
                          <Chip size="small" label={`Duration: ${session.durationMinutes ?? '-'} min`} />
                          <Chip size="small" label={`Intensity: ${session.intensity ?? '-'}`} />
                          <Chip size="small" label={`Activities: ${activities.length}`} />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {session?.notes ? (
                          <Typography variant="body2">{session.notes}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No session notes.
                          </Typography>
                        )}
                        <Box className="training-history-activity-list">
                          {activities.map((activity) => (
                            <Paper key={activity.id} variant="outlined" className="training-history-activity-item">
                              <Typography variant="subtitle2">
                                {getActivityName(activityNamesById, activity.activityId)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Duration: {activity.durationMinutes ?? 0} min
                              </Typography>
                              {activity.notes ? (
                                <Typography variant="body2">{activity.notes}</Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No activity notes.
                                </Typography>
                              )}
                            </Paper>
                          ))}
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
